import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/button/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { type ResMember } from '@/lib/queries/business/v1/types'
import { deleteUserMutation } from '@/lib/queries/business/v1/user/mutations'
import { cn } from '@/lib/utils'

export interface DeleteMemberDialogProps {
  open: boolean
  deletedMembers: ResMember[]
  onClose: VoidFunction
  onDeleteSuccess?: VoidFunction
}

export const DeleteMemberDialog = ({ open, deletedMembers, onClose, onDeleteSuccess }: DeleteMemberDialogProps) => {
  const { mutateAsync: deleteUser, isPending: isPendingDeleteUser } = useMutation(deleteUserMutation)

  const deletedMemberNames = deletedMembers.map(({ name }) => name)
  const isMultipleUsers = deletedMembers.length > 1

  const handleClickDeleteButton = async () => {
    await deleteUser(
      { userIds: deletedMembers.map(({ id }) => id) },
      {
        onSuccess: (response) => {
          setTimeout(async () => {
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v1.user.members.index],
            })
          }, 1000)
          onClose()
          onDeleteSuccess?.()

          const { failedUserIds } = response
          if (failedUserIds.length > 0) {
            const isFailedMultipleUsers = failedUserIds.length > 1
            toast.error(
              <span>
                {isFailedMultipleUsers ? 'Some members' : 'The member'}
                {`(${deletedMembers
                  .filter(({ id }) => failedUserIds.includes(id))
                  .map(({ name }) => name)
                  .join(', ')}) `}
                could not be deleted successfully. <br />
              </span>,
              {
                duration: 6000,
              }
            )
          } else {
            toast.success(`Selected ${isMultipleUsers ? 'members' : 'member'} deleted successfully.`)
          }
        },
        onError: () => {
          toast.error(`Failed to delete the selected ${isMultipleUsers ? 'members' : 'member'}.`)
        },
      }
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose()
        }
      }}
    >
      <DialogContent hideClose>
        <DialogHeader>
          <DialogTitle>{isMultipleUsers ? 'Deleting members' : 'Deleting member'}</DialogTitle>
          <DialogDescription className="sr-only">
            Confirming deletion of selected members. Cases that are only visible to{' '}
            {isMultipleUsers ? 'those users' : 'this user'} will remain visible only to Super Admin users.
          </DialogDescription>
        </DialogHeader>

        <p className={cn('text-[#64748B]')}>
          Are you sure you want to delete{' '}
          <span className={cn('font-semibold text-slate-800')}>{deletedMemberNames.join(', ')}</span>
          ?
          <br />
          Cases that are only visible to {isMultipleUsers ? 'those users' : 'this user'} will remain visible only to
          Super Admin users.
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" loading={isPendingDeleteUser} onClick={handleClickDeleteButton}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
